import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Link2,
  Unlink,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  RefreshCw,
  CreditCard,
  FileText,
  Calculator,
  AlertTriangle,
  Settings,
  ArrowRightLeft,
} from 'lucide-react';
import BillingDataReset from './BillingDataReset';

/**
 * BillingIntegrations - Superadmin component for managing billing integrations
 *
 * Manages connections to:
 * - Xero (accounting/invoicing)
 * - Stripe (payments) - status display only, configured via env vars
 * - TaxJar (tax calculations) - status display only, configured via env vars
 */
const BillingIntegrations = ({ currentUser }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [xeroStatus, setXeroStatus] = useState({
    configured: false,
    connected: false,
    sandbox_mode: true,
  });

  const [integrationsStatus, setIntegrationsStatus] = useState({
    stripe: { configured: false, sandbox_mode: true },
    xero: { configured: false, connected: false, sandbox_mode: true },
    taxjar: { configured: false, sandbox_mode: true },
    global_mode: 'sandbox',
  });

  // Mode switch state
  const [showModeSwitchModal, setShowModeSwitchModal] = useState(false);
  const [modeSwitchTarget, setModeSwitchTarget] = useState('production');
  const [modeSwitchConfirmation, setModeSwitchConfirmation] = useState('');
  const [switchingMode, setSwitchingMode] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const hasPermission = currentUser?.admin_permissions?.includes('billing_integrations');
  const canAccessIntegrations = isSuperAdmin || hasPermission;

  useEffect(() => {
    // Handle OAuth callback status from URL
    const xeroResult = searchParams.get('xero');
    if (xeroResult === 'connected') {
      setSuccess('Xero connected successfully!');
      searchParams.delete('xero');
      setSearchParams(searchParams);
    } else if (xeroResult === 'error') {
      const message = searchParams.get('message') || 'Unknown error';
      setError(`Xero connection failed: ${message}`);
      searchParams.delete('xero');
      searchParams.delete('message');
      setSearchParams(searchParams);
    }

    if (canAccessIntegrations) {
      fetchXeroStatus();
      fetchIntegrationsStatus();
    } else {
      setLoading(false);
    }
  }, [canAccessIntegrations, searchParams, setSearchParams]);

  const fetchIntegrationsStatus = async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/billing/integrations/status`,
        { headers: getAuthHeaders() }
      );
      setIntegrationsStatus(response.data);
    } catch (err) {
      console.error('Error fetching integrations status:', err);
      // Non-critical, don't show error
    }
  };

  const validateProductionSwitch = async () => {
    setValidating(true);
    setValidationResult(null);
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/billing/integrations/validate-production`,
        { headers: getAuthHeaders() }
      );
      setValidationResult(response.data);
    } catch (err) {
      console.error('Error validating production switch:', err);
      setValidationResult({
        ready: false,
        errors: [err.response?.data?.detail || 'Failed to validate'],
        warnings: [],
      });
    } finally {
      setValidating(false);
    }
  };

  const handleOpenModeSwitch = async (targetMode) => {
    setModeSwitchTarget(targetMode);
    setModeSwitchConfirmation('');
    setShowModeSwitchModal(true);

    // Validate if switching to production
    if (targetMode === 'production') {
      await validateProductionSwitch();
    } else {
      setValidationResult(null);
    }
  };

  const handleModeSwitch = async () => {
    const expectedPhrase = modeSwitchTarget === 'production'
      ? 'SWITCH TO PRODUCTION'
      : 'SWITCH TO SANDBOX';

    if (modeSwitchConfirmation !== expectedPhrase) {
      setError(`Please type "${expectedPhrase}" to confirm`);
      return;
    }

    setSwitchingMode(true);
    setError('');

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/billing/integrations/mode`,
        {
          sandbox: modeSwitchTarget === 'sandbox',
          confirmation_phrase: modeSwitchConfirmation,
        },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        setSuccess(
          `Successfully switched to ${modeSwitchTarget} mode. ` +
          (response.data.xero_disconnected
            ? 'Xero has been disconnected and will need to be reconnected.'
            : '')
        );
        setShowModeSwitchModal(false);
        setModeSwitchConfirmation('');
        fetchIntegrationsStatus();
        fetchXeroStatus();
      } else {
        setError(response.data.message || 'Failed to switch mode');
      }
    } catch (err) {
      console.error('Error switching mode:', err);
      setError(err.response?.data?.detail || 'Failed to switch mode');
    } finally {
      setSwitchingMode(false);
    }
  };

  const fetchXeroStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${getApiBase()}/billing/xero/status`,
        { headers: getAuthHeaders() }
      );
      setXeroStatus(response.data);
    } catch (err) {
      console.error('Error fetching Xero status:', err);
      // Don't show error if just not configured
      if (err.response?.status !== 403) {
        setError('Failed to fetch Xero status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConnectXero = async () => {
    setError('');
    setConnecting(true);

    try {
      const response = await axios.get(
        `${getApiBase()}/billing/xero/auth`,
        { headers: getAuthHeaders() }
      );
      const { authorization_url } = response.data;

      // Redirect to Xero authorization
      window.location.href = authorization_url;
    } catch (err) {
      console.error('Error initiating Xero connection:', err);
      setError(err.response?.data?.detail || 'Failed to initiate Xero connection');
      setConnecting(false);
    }
  };

  const handleDisconnectXero = async () => {
    if (!window.confirm('Are you sure you want to disconnect Xero? This will stop invoice syncing.')) {
      return;
    }

    setError('');
    setDisconnecting(true);

    try {
      await axios.post(
        `${getApiBase()}/billing/xero/disconnect`,
        {},
        { headers: getAuthHeaders() }
      );
      setSuccess('Xero disconnected successfully');
      setXeroStatus({ configured: false, connected: false, sandbox_mode: true });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error disconnecting Xero:', err);
      setError(err.response?.data?.detail || 'Failed to disconnect Xero');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRepairXero = async () => {
    setError('');
    setRepairing(true);

    try {
      const res = await axios.post(
        `${getApiBase()}/billing/xero/repair`,
        {},
        { headers: getAuthHeaders() }
      );
      setSuccess(res.data.message || 'Xero connection repaired');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error repairing Xero:', err);
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setError(`${detail.message}\n\nDiagnostics:\n${JSON.stringify(detail.diagnostics, null, 2)}`);
      } else {
        setError(detail || 'Failed to repair Xero connection');
      }
    } finally {
      setRepairing(false);
    }
  };

  if (!canAccessIntegrations) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center gap-2 text-yellow-800">
          <AlertCircle className="w-5 h-5" />
          <span>You need superadmin privileges or Billing & Integrations permission to manage billing integrations.</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen p-6 -m-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Billing Integrations</h2>
          <p className="text-sm text-gray-600 mt-1">
            Connect external services for billing, accounting, and tax compliance
          </p>
        </div>
        <button
          onClick={() => { fetchXeroStatus(); fetchIntegrationsStatus(); }}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Test Mode Banner */}
      {integrationsStatus.global_mode === 'sandbox' && (
        <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-800 font-medium">Test Mode Active</p>
              <p className="text-yellow-700 text-sm mt-1">
                All billing integrations are operating in sandbox/test mode.
                No real transactions will be processed. Test data can be reset below.
              </p>
            </div>
          </div>
        </div>
      )}

      {integrationsStatus.global_mode === 'production' && (
        <div className="p-4 bg-green-50 border border-green-300 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-800 font-medium">Production Mode Active</p>
              <p className="text-green-700 text-sm mt-1">
                All billing integrations are operating in production mode.
                Real transactions will be processed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mode Switch Section */}
      {isSuperAdmin && (
        <div className="border rounded-lg p-5 bg-white shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <ArrowRightLeft className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Integration Mode</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Switch all billing integrations between sandbox and production mode
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <span className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                    integrationsStatus.global_mode === 'sandbox'
                      ? 'bg-yellow-100 text-yellow-800'
                      : integrationsStatus.global_mode === 'production'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {integrationsStatus.global_mode === 'sandbox' && 'Sandbox Mode'}
                    {integrationsStatus.global_mode === 'production' && 'Production Mode'}
                    {integrationsStatus.global_mode === 'mixed' && 'Mixed Mode'}
                  </span>
                </div>
              </div>
            </div>
            <div>
              {integrationsStatus.global_mode === 'sandbox' ? (
                <button
                  onClick={() => handleOpenModeSwitch('production')}
                  disabled={!integrationsStatus.can_switch_to_production}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!integrationsStatus.can_switch_to_production ? 'Production API keys not configured' : ''}
                >
                  <Settings className="w-4 h-4" />
                  Switch to Production
                </button>
              ) : (
                <button
                  onClick={() => handleOpenModeSwitch('sandbox')}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded-md"
                >
                  <Settings className="w-4 h-4" />
                  Switch to Sandbox
                </button>
              )}
            </div>
          </div>
          {!integrationsStatus.can_switch_to_production && integrationsStatus.global_mode === 'sandbox' && (
            <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
              <p>
                <strong>Note:</strong> Production API keys are not configured.
                Set <code className="bg-gray-200 px-1 rounded">STRIPE_SECRET_KEY</code> and optionally{' '}
                <code className="bg-gray-200 px-1 rounded">TAXJAR_API_KEY</code> environment variables to enable production mode.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Status Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <pre className="text-red-700 text-sm whitespace-pre-wrap font-sans">{error}</pre>
          </div>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Integration Cards */}
      <div className="grid gap-4">

        {/* Xero Integration */}
        <div className="border rounded-lg p-5 bg-white shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">Xero</h3>
                  {xeroStatus.sandbox_mode && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                      Sandbox
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Sync invoices, payments, and contacts with Xero accounting
                </p>
                <div className="flex items-center gap-4 mt-3">
                  {xeroStatus.connected ? (
                    <span className="flex items-center gap-1.5 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Unlink className="w-4 h-4" />
                      Not connected
                    </span>
                  )}
                  <a
                    href="https://developer.xero.com/app/manage"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    Manage App <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {xeroStatus.connected ? (
                <>
                  <button
                    onClick={handleRepairXero}
                    disabled={repairing}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md disabled:opacity-50"
                    title="Re-fetch tenant ID and refresh token"
                  >
                    {repairing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Repair
                  </button>
                  <button
                    onClick={handleDisconnectXero}
                    disabled={disconnecting}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md disabled:opacity-50"
                  >
                    {disconnecting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Unlink className="w-4 h-4" />
                    )}
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={handleConnectXero}
                  disabled={connecting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  {connecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Link2 className="w-4 h-4" />
                  )}
                  Connect Xero
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stripe Integration (Status Only) */}
        <div className="border rounded-lg p-5 bg-white shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">Stripe</h3>
                  {integrationsStatus.stripe?.sandbox_mode ? (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                      Sandbox
                    </span>
                  ) : (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                      Production
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Payment processing, subscriptions, and invoicing
                </p>
                <div className="flex items-center gap-4 mt-3">
                  {integrationsStatus.stripe?.configured ? (
                    <span className="flex items-center gap-1.5 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      Configured
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-sm text-gray-500">
                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                      Not configured
                    </span>
                  )}
                  <a
                    href={integrationsStatus.stripe?.sandbox_mode
                      ? "https://dashboard.stripe.com/test/apikeys"
                      : "https://dashboard.stripe.com/apikeys"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    Dashboard <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Stripe is configured via <code className="bg-gray-100 px-1 rounded">
                {integrationsStatus.stripe?.sandbox_mode ? 'STRIPE_TEST_SECRET_KEY' : 'STRIPE_SECRET_KEY'}
              </code> environment variable.
            </p>
          </div>
        </div>

        {/* TaxJar Integration (Status Only) */}
        <div className="border rounded-lg p-5 bg-white shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Calculator className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">TaxJar</h3>
                  {integrationsStatus.taxjar?.sandbox_mode ? (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                      Sandbox
                    </span>
                  ) : integrationsStatus.taxjar?.configured ? (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                      Production
                    </span>
                  ) : (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                      Not Configured
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Automated sales tax calculations and compliance
                </p>
                <div className="flex items-center gap-4 mt-3">
                  {integrationsStatus.taxjar?.configured ? (
                    <span className="flex items-center gap-1.5 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      Configured
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-sm text-gray-500">
                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                      Not configured
                    </span>
                  )}
                  <a
                    href="https://app.taxjar.com/account#api-access"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    API Settings <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              TaxJar is configured via <code className="bg-gray-100 px-1 rounded">
                {integrationsStatus.taxjar?.sandbox_mode ? 'TAXJAR_SANDBOX_API_KEY' : 'TAXJAR_API_KEY'}
              </code> environment variable.
            </p>
          </div>
        </div>

      </div>

      {/* Billing Data Reset Section (Sandbox Mode Only) */}
      {integrationsStatus.global_mode === 'sandbox' && isSuperAdmin && (
        <div className="border rounded-lg p-5 bg-white shadow-sm">
          <BillingDataReset currentUser={currentUser} />
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Setup Instructions</h4>
        <ul className="text-sm text-gray-600 space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-medium">Xero:</span>
            Click "Connect Xero" to authorize. Make sure your Xero app has redirect URI configured to point to your backend callback endpoint (e.g.,{' '}
            <code className="bg-white px-1 rounded border text-xs">{'{BACKEND_URL}'}/api/billing/xero/callback</code>)
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-600 font-medium">Stripe:</span>
            Set <code className="bg-white px-1 rounded border text-xs">STRIPE_TEST_SECRET_KEY</code> in Render environment
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-600 font-medium">TaxJar:</span>
            Set <code className="bg-white px-1 rounded border text-xs">TAXJAR_SANDBOX_API_KEY</code> in Render environment
          </li>
        </ul>
      </div>

      {/* Mode Switch Confirmation Modal */}
      {showModeSwitchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className={`flex items-center gap-3 mb-4 ${
              modeSwitchTarget === 'production' ? 'text-green-600' : 'text-yellow-600'
            }`}>
              <ArrowRightLeft className="w-6 h-6" />
              <h3 className="text-lg font-semibold">
                Switch to {modeSwitchTarget === 'production' ? 'Production' : 'Sandbox'} Mode
              </h3>
            </div>

            <div className="space-y-4 text-sm text-gray-600">
              {modeSwitchTarget === 'production' ? (
                <>
                  <p>You are about to switch all billing integrations to <strong>production mode</strong>.</p>

                  {validating ? (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Validating configuration...
                    </div>
                  ) : validationResult && (
                    <div className="space-y-2">
                      {validationResult.errors?.length > 0 && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded">
                          <p className="font-medium text-red-800 mb-1">Errors:</p>
                          <ul className="list-disc ml-4 text-red-700">
                            {validationResult.errors.map((err, idx) => (
                              <li key={idx}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {validationResult.warnings?.length > 0 && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                          <p className="font-medium text-yellow-800 mb-1">Warnings:</p>
                          <ul className="list-disc ml-4 text-yellow-700">
                            {validationResult.warnings.map((warn, idx) => (
                              <li key={idx}>{warn}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                    <p className="font-medium text-red-800">Warning:</p>
                    <ul className="list-disc ml-4 mt-1 text-red-700">
                      <li>Real payments will be processed</li>
                      <li>Transactions cannot be easily reversed</li>
                      <li>Xero will be disconnected and need reconnection to your production organization</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <p>You are about to switch all billing integrations back to <strong>sandbox mode</strong>.</p>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="font-medium text-yellow-800">Note:</p>
                    <ul className="list-disc ml-4 mt-1 text-yellow-700">
                      <li>Test API keys will be used</li>
                      <li>No real transactions will be processed</li>
                      <li>Xero will be disconnected and need reconnection to your demo company</li>
                    </ul>
                  </div>
                </>
              )}

              <div>
                <p className="mb-2">
                  Type <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-sm">
                    {modeSwitchTarget === 'production' ? 'SWITCH TO PRODUCTION' : 'SWITCH TO SANDBOX'}
                  </code> to confirm:
                </p>
                <input
                  type="text"
                  value={modeSwitchConfirmation}
                  onChange={(e) => setModeSwitchConfirmation(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md font-mono"
                  placeholder={modeSwitchTarget === 'production' ? 'SWITCH TO PRODUCTION' : 'SWITCH TO SANDBOX'}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModeSwitchModal(false);
                  setModeSwitchConfirmation('');
                  setValidationResult(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleModeSwitch}
                disabled={
                  switchingMode ||
                  modeSwitchConfirmation !== (modeSwitchTarget === 'production' ? 'SWITCH TO PRODUCTION' : 'SWITCH TO SANDBOX') ||
                  (modeSwitchTarget === 'production' && validationResult && !validationResult.ready)
                }
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                  modeSwitchTarget === 'production'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                {switchingMode ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRightLeft className="w-4 h-4" />
                )}
                Switch Mode
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingIntegrations;
